import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import { useCallback, useEffect, useRef, useState } from "react";

import { createClient } from "~/lib/supabase";

function getSupabase(ref: React.RefObject<SupabaseClient | null>): SupabaseClient {
	if (!ref.current) {
		ref.current = createClient();
	}
	return ref.current;
}

export function useAuth() {
	const supabaseRef = useRef<SupabaseClient | null>(null);
	const [user, setUser] = useState<User | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const supabase = getSupabase(supabaseRef);

		supabase.auth.getSession().then(({ data: { session } }) => {
			setSession(session);
			setUser(session?.user ?? null);
			setLoading(false);
		});

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			setSession(session);
			setUser(session?.user ?? null);
		});

		return () => subscription.unsubscribe();
	}, []);

	const signInWithEmail = useCallback(async (email: string, password: string) => {
		return getSupabase(supabaseRef).auth.signInWithPassword({ email, password });
	}, []);

	const signUp = useCallback(async (email: string, password: string) => {
		return getSupabase(supabaseRef).auth.signUp({ email, password });
	}, []);

	const signInWithOAuth = useCallback(async (provider: "google") => {
		return getSupabase(supabaseRef).auth.signInWithOAuth({
			provider,
			options: { redirectTo: window.location.origin },
		});
	}, []);

	const signOut = useCallback(async () => {
		return getSupabase(supabaseRef).auth.signOut();
	}, []);

	/** Exchange a recovery token (from the password-reset e-mail) for a session */
	const verifyRecoveryToken = useCallback(async (tokenHash: string) => {
		return getSupabase(supabaseRef).auth.verifyOtp({ type: "recovery", token_hash: tokenHash });
	}, []);

	const updatePassword = useCallback(async (password: string) => {
		return getSupabase(supabaseRef).auth.updateUser({ password });
	}, []);

	return {
		user,
		session,
		loading,
		signInWithEmail,
		signUp,
		signInWithOAuth,
		signOut,
		verifyRecoveryToken,
		updatePassword,
	};
}
